import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Alert } from 'antd';

import { useSelector, usePrerenderUserId, useRenderUsagePercentage } from '../../../hooks/usePrerenderUser';
import useSuggestedPlan from '../hooks/useSuggestedPlan';
import { useTrial } from '~features/FreeTrial/useTrial';
import { EmailTemplates } from '~features/CustomerSupport';

function RenderCounterAlert() {
  const userId = usePrerenderUserId();
  const amountOf429Renders = useSelector((state) => state.prerenderUser.amountOf429Renders);
  const planName = useSelector((state) => state.prerenderUser.plan.name);
  const { isHalfwayThroughTrial, isInTrial } = useTrial();
  const suggestedPlan = useSuggestedPlan();

  const is429OverLimit = amountOf429Renders > 0;
  const renderUsagePercentage = useRenderUsagePercentage();

  if (isInTrial) {
    if (renderUsagePercentage >= 115) {
      const trialRender115Link = EmailTemplates.buildFreeTrial115UsageHref({
        suggestedPlan: suggestedPlan?.name || '-',
        accountId: userId,
        currentPlan: planName,
      });
      return (
        <Alert
          showIcon
          type="error"
          message={
            <>
              <Typography.Paragraph>Your trial renders are finished and rendering has stopped.</Typography.Paragraph>
              {suggestedPlan && (
                <Typography.Paragraph>
                  Recommended plan based on your usage:{' '}
                  <Link to={`/billing/plans?selectedPlan=${suggestedPlan.id}`}>{suggestedPlan.name}</Link>.
                </Typography.Paragraph>
              )}
              <Typography.Text>
                If you need assistance or want to explore upgrade options, please contact our Customer Success team at{' '}
                <Typography.Link href={trialRender115Link}>support@prerender.io</Typography.Link>.
              </Typography.Text>
            </>
          }
        />
      );
    }

    if (renderUsagePercentage >= 100) {
      const trialRender100Link = EmailTemplates.buildFreeTrial100UsageHref({
        suggestedPlan: suggestedPlan?.name || '-',
        accountId: userId,
        currentPlan: planName,
      });
      return (
        <Alert
          showIcon
          type="error"
          message={
            <>
              <Typography.Paragraph>
                You’ve used your free trial renders. A 15% buffer remains before rendering stops.
              </Typography.Paragraph>
              {suggestedPlan && (
                <Typography.Paragraph>
                  Recommended plan based on your usage:{' '}
                  <Link to={`/billing/plans?selectedPlan=${suggestedPlan.id}`}>{suggestedPlan.name}</Link>.
                </Typography.Paragraph>
              )}
              <Typography.Text>
                If you’d like to discuss options, you can also reach out to our Customer Success team at{' '}
                <Typography.Link href={trialRender100Link}>support@prerender.io</Typography.Link>.
              </Typography.Text>
            </>
          }
        />
      );
    }

    if (isHalfwayThroughTrial) {
      return (
        <Alert
          showIcon
          type="warning"
          message={'Keep the momentum going! Upgrade before your trial ends to avoid disrupting progress.'}
        />
      );
    }
  }

  if (renderUsagePercentage >= 100) {
    return (
      <Alert
        showIcon
        type="error"
        message={
          <>
            <Typography.Paragraph style={suggestedPlan ? {} : { marginBottom: 0 }}>
              Heads up - you&apos;re over your render limit! Upgrade your plan to maintain your momentum.
            </Typography.Paragraph>
            {suggestedPlan && (
              <Typography.Text>
                Recommended plan based on your usage:{' '}
                <Link to={`/billing/plans?selectedPlan=${suggestedPlan.id}`}>{suggestedPlan.name}</Link>
              </Typography.Text>
            )}
          </>
        }
      />
    );
  }

  if (is429OverLimit) {
    return (
      <Alert
        showIcon
        type="error"
        message={
          <>
            <Typography.Text>
              Uh oh, you’ve reached your render limit. Upgrade your account to prevent broken link previews, outdated
              content, or 429 errors.
            </Typography.Text>
          </>
        }
      />
    );
  }

  return null;
}

export default RenderCounterAlert;
